import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../utils/api';

/**
 * Turnos de aprobación de pagos.
 *
 * Se marcan varios días y luego se elige a quién: así se arma un turno entero
 * de un tirón en vez de día por día.
 *
 * El turno decide a quién le llega el aviso cuando entra un comprobante, no
 * quién puede aprobarlo. Cualquier super admin sigue pudiendo revisar todo; un
 * día sin responsable avisa a todos, que es mejor que dejar un pago esperando
 * porque nadie se enteró.
 */

// Colores por persona. Cada día muestra además iniciales y nombre, así que la
// identidad nunca depende solo del color.
const COLORES = [
  { color: '#4f46e5', fondo: 'rgba(79,70,229,.13)' },
  { color: '#0d9488', fondo: 'rgba(13,148,136,.14)' },
  { color: '#b45309', fondo: 'rgba(180,83,9,.13)' },
  { color: '#be123c', fondo: 'rgba(190,18,60,.12)' },
];

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
               'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DOW = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const iniciales = (nombre = '') => nombre.trim().split(/\s+/).slice(0, 2).map(p => p[0] || '').join('').toUpperCase();
const dosDig = (n) => String(n).padStart(2, '0');

export default function TurnosAprobacion({ esMovil, avisar }) {
  const [datos, setDatos]     = useState(null);
  const [cargando, setCarga]  = useState(true);
  const [fallo, setFallo]     = useState('');
  const [seleccion, setSel]   = useState(new Set());
  const [periodo, setPeriodo] = useState(null);   // { anio, mes }
  const [guardando, setGuard] = useState(false);

  const cargar = useCallback(async (p) => {
    setCarga(true);
    try {
      const r = await api.get('/payments/turnos', { params: p || {} });
      setDatos(r.data);
      setPeriodo({ anio: r.data.anio, mes: r.data.mes });
      setFallo('');
    } catch (err) {
      setFallo(err.response?.status === 404
        ? 'El servidor todavía no tiene este módulo: falta subir routes/payments.js y services/turnos.js y reiniciar.'
        : (err.response?.data?.error || 'No se pudieron cargar los turnos'));
    } finally { setCarga(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const colorDe = useCallback((usuarioId) => {
    const i = (datos?.personas || []).findIndex(p => p.id === usuarioId);
    return COLORES[i < 0 ? 0 : i % COLORES.length];
  }, [datos]);

  const dias = useMemo(() => {
    if (!periodo) return [];
    const { anio, mes } = periodo;
    const total = new Date(anio, mes, 0).getDate();
    // getDay() da 0 para domingo; el calendario arranca en lunes.
    const primero = (new Date(anio, mes - 1, 1).getDay() + 6) % 7;
    return { total, primero };
  }, [periodo]);

  const mover = (delta) => {
    const m = periodo.mes + delta;
    const anio = periodo.anio + (m < 1 ? -1 : m > 12 ? 1 : 0);
    const mes = m < 1 ? 12 : m > 12 ? 1 : m;
    setSel(new Set());
    cargar({ anio, mes });
  };

  const fechaDe = (d) => `${periodo.anio}-${dosDig(periodo.mes)}-${dosDig(d)}`;

  const alternar = (d) => {
    setSel(s => {
      const n = new Set(s);
      n.has(d) ? n.delete(d) : n.add(d);
      return n;
    });
  };

  const asignar = async (usuarioId) => {
    if (!seleccion.size) return;
    setGuard(true);
    try {
      const r = await api.put('/payments/turnos', {
        fechas: [...seleccion].map(fechaDe),
        usuario_id: usuarioId,
      });
      avisar(r.data?.message || 'Turnos actualizados');
      setSel(new Set());
      cargar(periodo);
    } catch (err) {
      avisar(err.response?.data?.error || 'No se pudieron guardar los turnos', true);
    } finally { setGuard(false); }
  };

  // Reglas rápidas: cubren el reparto típico y luego se ajusta a mano.
  const regla = async (tipo, usuarioId) => {
    const fechas = [];
    for (let d = 1; d <= dias.total; d++) {
      const dow = (dias.primero + d - 1) % 7;
      const finde = dow >= 5;
      if (tipo === 'semana' && !finde) fechas.push(fechaDe(d));
      if (tipo === 'finde'  && finde)  fechas.push(fechaDe(d));
      if (tipo === 'todo')             fechas.push(fechaDe(d));
    }
    if (!fechas.length) return;
    setGuard(true);
    try {
      const r = await api.put('/payments/turnos', { fechas, usuario_id: usuarioId });
      avisar(r.data?.message || 'Turnos actualizados');
      setSel(new Set());
      cargar(periodo);
    } catch (err) {
      avisar(err.response?.data?.error || 'No se pudieron guardar los turnos', true);
    } finally { setGuard(false); }
  };

  if (cargando && !datos) {
    return <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando turnos...</div>;
  }
  if (fallo) {
    return (
      <div className="card" style={{ padding: '2.5rem 2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.6rem', marginBottom: 8 }}>⚠️</div>
        <p style={{ margin: '0 auto', maxWidth: '54ch', fontSize: '0.86rem', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>{fallo}</p>
        <button className="btn btn-secondary" style={{ marginTop: 14 }} onClick={() => cargar()}>Reintentar</button>
      </div>
    );
  }

  const { asignaciones, personas, hoy } = datos;
  const esMesDeHoy = hoy.slice(0, 7) === `${periodo.anio}-${dosDig(periodo.mes)}`;
  const diaDeHoy = esMesDeHoy ? Number(hoy.slice(8, 10)) : null;
  const turnoHoy = asignaciones[hoy];
  const sinAsignar = dias.total - Object.keys(asignaciones).length;

  const conteo = (id) => Object.values(asignaciones).filter(a => a.usuario_id === id).length;

  return (
    <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: esMovil ? '1fr' : 'minmax(0,1fr) 300px', alignItems: 'start' }}>
      {/* ── calendario ── */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0.85rem 1.1rem', borderBottom: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-icon" onClick={() => mover(-1)} disabled={guardando}>‹</button>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 700, minWidth: 170, textTransform: 'capitalize' }}>
            {MESES[periodo.mes - 1]} {periodo.anio}
          </span>
          <button className="btn btn-secondary btn-icon" onClick={() => mover(1)} disabled={guardando}>›</button>

          {personas.length > 0 && (
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" style={{ fontSize: '0.78rem' }} disabled={guardando}
                      onClick={() => regla('semana', personas[0].id)}>
                Entre semana → {personas[0].nombre.split(' ')[0]}
              </button>
              {personas[1] && (
                <button className="btn btn-secondary" style={{ fontSize: '0.78rem' }} disabled={guardando}
                        onClick={() => regla('finde', personas[1].id)}>
                  Fines de semana → {personas[1].nombre.split(' ')[0]}
                </button>
              )}
              <button className="btn btn-ghost" style={{ fontSize: '0.78rem' }} disabled={guardando}
                      onClick={() => regla('todo', null)}>Limpiar mes</button>
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
          {DOW.map(d => (
            <div key={d} style={{
              fontSize: '0.66rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
              color: 'var(--color-text-faint)', textAlign: 'center', padding: '9px 0',
              borderBottom: '1px solid var(--color-border)',
            }}>{d}</div>
          ))}

          {Array.from({ length: dias.primero }).map((_, i) => (
            <div key={`v${i}`} style={{ background: 'var(--color-surface-2)', borderRight: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)' }} />
          ))}

          {Array.from({ length: dias.total }).map((_, i) => {
            const d = i + 1;
            const dow = (dias.primero + d - 1) % 7;
            const asig = asignaciones[fechaDe(d)];
            const sel = seleccion.has(d);
            const c = asig ? colorDe(asig.usuario_id) : null;
            return (
              <div key={d} onClick={() => alternar(d)} style={{
                minHeight: esMovil ? 62 : 78, padding: '6px 7px', cursor: 'pointer', userSelect: 'none',
                borderRight: (dow === 6) ? 0 : '1px solid var(--color-border)',
                borderBottom: '1px solid var(--color-border)',
                background: sel ? 'var(--color-accent-subtle)' : 'var(--color-surface)',
                boxShadow: sel ? 'inset 0 0 0 2px var(--color-accent)' : 'none',
                position: 'relative',
              }}>
                <div style={{
                  fontSize: '0.82rem', fontWeight: 600,
                  color: dow >= 5 ? 'var(--color-text-muted)' : 'var(--color-text)',
                  ...(d === diaDeHoy ? {
                    background: 'var(--color-text)', color: '#fff', borderRadius: '50%',
                    width: 21, height: 21, display: 'grid', placeItems: 'center', fontSize: '0.74rem',
                  } : {}),
                }}>{d}</div>

                {asig && (
                  <span style={{
                    marginTop: 5, fontSize: '0.68rem', fontWeight: 600, padding: '2px 6px',
                    borderRadius: 5, background: c.fondo, color: c.color,
                    display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%',
                  }}>
                    <span style={{
                      width: 14, height: 14, borderRadius: '50%', background: c.color, color: '#fff',
                      fontSize: '0.55rem', display: 'grid', placeItems: 'center', flexShrink: 0,
                    }}>{iniciales(asig.nombre)}</span>
                    {!esMovil && (
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {(asig.nombre || '').split(' ')[0]}
                      </span>
                    )}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* barra de selección */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '0.8rem 1.1rem', borderTop: '1px solid var(--color-border)', background: 'var(--color-surface-2)',
        }}>
          {seleccion.size === 0 ? (
            <span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
              Haz clic en los días que quieras asignar. Puedes elegir varios antes de escoger a la persona.
            </span>
          ) : (
            <>
              <strong style={{ fontSize: '0.84rem' }}>
                {seleccion.size === 1 ? '1 día seleccionado' : `${seleccion.size} días seleccionados`}
              </strong>
              <span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>Asignar a:</span>
              <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginLeft: 'auto' }}>
                {personas.map((p, i) => {
                  const c = COLORES[i % COLORES.length];
                  return (
                    <button key={p.id} onClick={() => asignar(p.id)} disabled={guardando} style={{
                      border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                      padding: '6px 12px', borderRadius: 99, background: c.fondo, color: c.color,
                      display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'inherit',
                    }}>
                      <span style={{
                        width: 17, height: 17, borderRadius: '50%', background: c.color, color: '#fff',
                        fontSize: '0.6rem', display: 'grid', placeItems: 'center',
                      }}>{iniciales(p.nombre)}</span>
                      {p.nombre.split(' ')[0]}
                    </button>
                  );
                })}
                <button className="btn btn-ghost" style={{ fontSize: '0.8rem' }} disabled={guardando}
                        onClick={() => asignar(null)}>Quitar</button>
                <button className="btn btn-ghost" style={{ fontSize: '0.8rem' }}
                        onClick={() => setSel(new Set())}>Cancelar</button>
              </span>
            </>
          )}
        </div>
      </div>

      {/* ── panel ── */}
      <div>
        <div className="card" style={{
          padding: '0.9rem 1.1rem', marginBottom: '0.85rem',
          borderLeft: `4px solid ${turnoHoy ? colorDe(turnoHoy.usuario_id).color : 'var(--color-warning)'}`,
        }}>
          <div style={{ fontSize: '0.66rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-text-faint)' }}>
            De turno hoy
          </div>
          <div style={{ fontSize: '1.05rem', fontWeight: 700, marginTop: 2, color: turnoHoy ? colorDe(turnoHoy.usuario_id).color : 'var(--color-warning)' }}>
            {turnoHoy ? turnoHoy.nombre : 'Sin asignar'}
          </div>
          <p style={{ margin: '3px 0 0', fontSize: '0.78rem', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
            {turnoHoy ? 'Recibe los avisos de los comprobantes de hoy.' : 'Los avisos de hoy van a todos los super admins.'}
          </p>
        </div>

        <div className="card" style={{ padding: '0.9rem 1.1rem', marginBottom: '0.85rem' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: '0.92rem' }}>Reparto del mes</h3>
          {personas.map((p, i) => {
            const c = COLORES[i % COLORES.length];
            const n = conteo(p.id);
            return (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0', borderBottom: '1px solid var(--color-border)' }}>
                <span style={{
                  width: 28, height: 28, borderRadius: '50%', background: c.color, color: '#fff',
                  fontSize: '0.7rem', fontWeight: 700, display: 'grid', placeItems: 'center', flexShrink: 0,
                }}>{iniciales(p.nombre)}</span>
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ display: 'block', fontSize: '0.84rem', fontWeight: 500 }}>{p.nombre}</span>
                  {!p.tiene_celular && (
                    <span style={{ fontSize: '0.72rem', color: 'var(--color-warning)' }}>Sin celular cargado</span>
                  )}
                </span>
                <strong style={{ fontSize: '0.95rem' }}>{n}</strong>
              </div>
            );
          })}
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0' }}>
            <span style={{
              width: 28, height: 28, borderRadius: '50%', background: 'var(--color-text-faint)', color: '#fff',
              fontSize: '0.8rem', display: 'grid', placeItems: 'center', flexShrink: 0,
            }}>—</span>
            <span style={{ flex: 1, fontSize: '0.84rem' }}>Sin asignar</span>
            <strong style={{ fontSize: '0.95rem', color: sinAsignar ? 'var(--color-warning)' : 'var(--color-text)' }}>{sinAsignar}</strong>
          </div>
        </div>

        <div className="card" style={{ padding: '0.9rem 1.1rem' }}>
          <h3 style={{ margin: '0 0 6px', fontSize: '0.92rem' }}>Cómo funciona</h3>
          <p style={{ margin: '0 0 6px', fontSize: '0.79rem', color: 'var(--color-text-muted)', lineHeight: 1.55 }}>
            Cuando un negocio sube un comprobante, el aviso por WhatsApp le llega
            <strong> solo a quien esté de turno</strong> ese día.
          </p>
          <p style={{ margin: 0, fontSize: '0.79rem', color: 'var(--color-text-muted)', lineHeight: 1.55 }}>
            El turno no bloquea: cualquier super admin puede aprobar cualquier pago. Un día sin
            responsable avisa a todos.
          </p>
        </div>
      </div>
    </div>
  );
}
